import { useParams, usePathname } from "next/navigation";
import Link from "next/link";
import { useQueries } from "@tanstack/react-query";
import { Alert, Spinner, TabContent } from "react-bootstrap";
import styled from "styled-components";

import {
  AttributesView,
  BackLink,
  Card,
  CardGrid,
  CopyValue,
  DynamicTab,
  DynamicTabs,
  Field,
  FieldType,
  HeaderDetail,
  Inspector,
  Page,
  ScrollingTextArea,
  toAttributes,
} from "@bagel-web/components";

import {
  AgentConfig,
  CommonAPI,
  SessionDetails,
  util,
  useFilters,
  getUserContext,
} from "@bagel-web/common";

import { agentQueries, sessionQueries } from "../queries";
import AgentConfigCards from "./AgentConfigCards";
import AgentIcon from "./AgentIcon";
import { getFriendlyAgentName } from "../util";
import AgentHistory, { AgentHistoryTab } from "./AgentHistory";
import { AgentMomentCustomPrefetch } from "./useAgentMomentPrefetch";
import AgentCloneHistory from "./AgentCloneHistory";
import { AgentViewPreferencesProvider } from "./viewPreferences/agentViewPreferences";

const StyledTabs = styled(DynamicTabs)`
  --bs-nav-link-color: var(--bs-link-color);
  --bs-nav-link-hover-color: var(--bs-link-hover-color);
`;

const fields: Array<Field> = [
  {
    name: "selectedAgentTab",
    label: "Selected Agent Tab",
    type: FieldType.String,
    hidden: true,
  },
];

const AgentDetailsPage = ({
  commonAPI,
  historyTabs,
  showProject = false,
  historyCustomPrefetch,
}: {
  commonAPI: CommonAPI;
  historyTabs?: Array<AgentHistoryTab>;
  showProject?: boolean;
  historyCustomPrefetch?: AgentMomentCustomPrefetch;
}) => {
  const { sessionId, agentId } = useParams<{
    sessionId: string;
    agentId: string;
  }>();

  const { filterCriteria, handleFilterChange } = useFilters(fields);
  const setSelectedTab = (selectedTab: string) => {
    handleFilterChange("selectedAgentTab", selectedTab);
  };

  const userContext = getUserContext();
  const pathname = usePathname();

  const [
    {
      data: agent,
      error: agentError,
      isPending: agentIsPending,
      isError: agentIsError,
    },
    {
      data: session,
      error: sessionError,
      isPending: sessionIsPending,
      isError: sessionIsError,
    },
  ] = useQueries({
    queries: [
      {
        queryKey: agentQueries.get(sessionId, agentId),
        queryFn: () => commonAPI.fetchAgentDetails({ sessionId, agentId }),
      },
      {
        queryKey: sessionQueries.get(sessionId),
        queryFn: () => commonAPI.fetchSession(sessionId),
      },
    ],
  });

  const agentConfig: AgentConfig | undefined = agent?.config;
  const agentName = getFriendlyAgentName(agent);

  const isPending = agentIsPending || sessionIsPending;
  const isError = agentIsError || sessionIsError;
  const error = agentError || sessionError;

  return (
    <AgentViewPreferencesProvider>
      <Page title={agentId}>
        <Page.Body>
          <div className="mb-3">
            <BackLink href={`/sessions/${sessionId}`} LinkComponent={Link}>
              Session Details
            </BackLink>
            <h2 style={{ display: "flex", gap: ".8rem", alignItems: "center" }}>
              <AgentIcon agent={agent} />
              {!agentIsPending && !agentIsError && (
                <span>{agentName || "Agent Details"}</span>
              )}
            </h2>

            <HeaderDetail>
              <HeaderDetail.Field label="Agent ID">
                <CopyValue>{agentId}</CopyValue>
              </HeaderDetail.Field>

              <HeaderDetail.Field label="Session ID">
                <Inspector
                  View={SessionDetails}
                  viewProps={{ session: session || null }}
                  showLoading={sessionIsPending}
                >
                  <CopyValue>{sessionId}</CopyValue>
                </Inspector>
              </HeaderDetail.Field>

              {showProject && session && (
                <>
                  <HeaderDetail.Field label="Organization">
                    <Link href={`/organizations/${session.organization}`}>
                      {session.organization}
                    </Link>
                  </HeaderDetail.Field>

                  <HeaderDetail.Field label="Project">
                    <Link
                      href={`/organizations/${session.organization}/projects`}
                    >
                      {session.project_name}
                    </Link>
                  </HeaderDetail.Field>
                </>
              )}

              {agentConfig && (
                <>
                  <HeaderDetail.Field label="Creation Time">
                    {util.formatTimestamp(agentConfig.created)}
                  </HeaderDetail.Field>

                  {agentConfig.destroyed && (
                    <HeaderDetail.Field label="Destroyed Time">
                      {util.formatTimestamp(agentConfig.destroyed)}
                    </HeaderDetail.Field>
                  )}

                  <HeaderDetail.Field label="Metadata">
                    <AttributesView
                      attributes={toAttributes(agent?.metadata)}
                    />
                  </HeaderDetail.Field>
                </>
              )}
            </HeaderDetail>
          </div>
          {isPending && !isError && <Spinner />}
          {isError && (
            <Alert variant="warning">
              Error loading agent details:<div>{error?.message}</div>
            </Alert>
          )}
          {!isPending && !isError && agent && session?.organization && (
            <>
              {userContext.projectId !== session.project_id && !showProject && (
                <Alert className="mt-2" variant="warning">
                  {`This Agent belongs to the Project `}
                  <Link
                    href={`/switch-project/${session.project_id}?path=${pathname}`}
                  >
                    {session.project_name}
                  </Link>
                  {`.`}
                </Alert>
              )}
              <StyledTabs
                className="mb-3"
                variant="underline"
                onSelectTab={setSelectedTab}
                selectedTab={filterCriteria.selectedAgentTab?.value}
              >
                <DynamicTab
                  eventKey="history"
                  iconClass="bi-clipboard2-pulse-fill"
                  title="Agent History"
                >
                  <TabContent>
                    <AgentHistory
                      organization={session?.organization}
                      sessionId={sessionId}
                      parentSessionId={session?.parent_session_id}
                      originMomentId={session?.origin_moment_id}
                      agentId={agentId}
                      commonAPI={commonAPI}
                      tabs={historyTabs}
                      customPrefetch={historyCustomPrefetch}
                      agentConfig={agentConfig}
                    />
                  </TabContent>
                </DynamicTab>

                <DynamicTab
                  eventKey="config"
                  iconClass="bi-person-fill-gear"
                  title="Agent Configuration"
                >
                  <TabContent>
                    <h4>Role</h4>
                    <CardGrid unitWidth={800} maxColumns={2}>
                      <Card title="Core Instructions" id="role-core">
                        <ScrollingTextArea className="pre-wrap">
                          {agent.role_config?.core}
                        </ScrollingTextArea>
                      </Card>
                      <Card title="Characterization" id="role-characterization">
                        <ScrollingTextArea className="pre-wrap">
                          {agent.role_config?.characterization}
                        </ScrollingTextArea>
                      </Card>
                    </CardGrid>
                    <h4>Other Configuration</h4>
                    <CardGrid unitWidth={800} maxColumns={2}>
                      <AgentConfigCards agentConfig={agentConfig} />
                    </CardGrid>
                  </TabContent>
                </DynamicTab>

                <DynamicTab
                  eventKey="cloned-agents"
                  iconClass="bi-diagram-3-fill"
                  title="Clone History"
                  mountOnEnter={true}
                >
                  <TabContent>
                    <AgentCloneHistory
                      sessionId={sessionId}
                      agentId={agentId}
                      commonAPI={commonAPI}
                    />
                  </TabContent>
                </DynamicTab>
              </StyledTabs>
            </>
          )}
        </Page.Body>
      </Page>
    </AgentViewPreferencesProvider>
  );
};

export default AgentDetailsPage;
